import { ArrowRight, Info, Plus, Shield, TrendingUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, Input, Text, Tooltip } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

import { type OrganicTier, type TierProtection } from '../domain/types';
import { AffiliateTierSelect } from './AffiliateTierSelect';
import { TierIncentivesCollapsible } from './TierIncentivesCollapsible';

const DEFAULT_TIER_TOOLTIP = 'This is the default tier, assigned to all affiliates by default.';

export interface PendingProtection {
  tier_id: string;
  days: number;
  /** When true, `days` represents extra days added to the current `ends_at`. */
  isExtension?: boolean;
}

interface TierOption {
  id: string;
  name: string;
  rank: number;
  audienceId: string | null;
  requiresManualProjectApproval: boolean;
}

interface TierProtectionCardProps {
  projectId: string;
  organicTier: OrganicTier | null;
  currentTier: OrganicTier | null;
  protections: TierProtection[];
  tiers: TierOption[];
  onPendingChange: (pending: PendingProtection | null, clearing: boolean, incomplete: boolean) => void;
  isLoading: boolean;
  addProtectionLabel?: string;
  addProtectionHelper?: React.ReactNode;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getDaysBetween(from: string, to: string): number {
  const start = new Date(from);
  const end = new Date(to);
  return Math.max(0, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
}

function ProtectionPeriodInput({
  tierId,
  initialDays,
  createdAt,
  onDaysChange,
  onClear,
  isLoading,
}: {
  tierId: string;
  initialDays: number | null;
  createdAt: string | null;
  onDaysChange: (tierId: string, days: number | null) => void;
  onClear: () => void;
  isLoading: boolean;
}) {
  const [days, setDays] = useState(initialDays?.toString() ?? '');

  const parsedDays = parseInt(days, 10);
  const isValid = parsedDays >= 1 && parsedDays <= 365;

  const handleDaysChange = (value: string) => {
    setDays(value);
    const parsed = parseInt(value, 10);
    if (parsed >= 1 && parsed <= 365) {
      onDaysChange(tierId, parsed);
    } else {
      onDaysChange(tierId, null);
    }
  };

  const baseDate = createdAt ? new Date(createdAt) : new Date();

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <Card variant="subcard" className="tw:px-4 tw:py-3 tw:gap-3">
        <div className="tw:flex tw:items-center tw:gap-4">
          <div className="tw:flex tw:items-center tw:gap-1">
            <Text variant="body-sm" weight="semibold" className="tw:whitespace-nowrap">
              Protection period <span className="tw:text-red-500">*</span>
            </Text>
            <Tooltip content="Days that the user will remain in this tier. After that period, they will be automatically assigned to a tier by evaluating the audiences conditions.">
              <Info className="tw:h-3.5 tw:w-3.5 tw:text-muted-foreground tw:cursor-help" aria-hidden="true" />
            </Tooltip>
          </div>
          <div className="tw:w-24 tw:shrink-0 tw:ml-auto">
            <Input
              type="number"
              min={1}
              max={365}
              size="sm"
              value={days}
              onChange={(e) => handleDaysChange(e.target.value)}
              placeholder="—"
              style={{ textAlign: 'right' }}
              rightSlot={
                <Text variant="body-sm" color="muted">
                  days
                </Text>
              }
            />
          </div>
        </div>
      </Card>

      <div className="tw:flex tw:items-center tw:gap-2">
        <button
          type="button"
          onClick={onClear}
          disabled={isLoading}
          className="tw:flex tw:items-center tw:gap-1.5 tw:text-error-main hover:tw:text-error-main/80 tw:transition-colors"
        >
          <X size={16} />
          <Text variant="body-sm" className="tw:text-inherit">
            Remove protection
          </Text>
        </button>
        {isValid && (
          <Text
            variant="body-md"
            className={cn(
              'tw:ml-auto tw:whitespace-nowrap tw:flex tw:items-center tw:gap-1.5 tw:transition-colors tw:text-fuul-blue',
            )}
          >
            {formatDate(baseDate)} <ArrowRight className="tw:inline tw:h-3 tw:w-3" aria-hidden="true" />{' '}
            {(() => {
              const end = new Date(baseDate);
              end.setDate(end.getDate() + parsedDays);
              return formatDate(end);
            })()}
          </Text>
        )}
      </div>
    </div>
  );
}

function ProtectionSection({
  protection,
  displayTierId,
  organicTier,
  isPerformanceTier,
  tiers,
  tierChanged,
  onDaysChange,
  onExtend,
  onTierChange,
  onClear,
  isLoading,
}: {
  protection: TierProtection;
  displayTierId: string;
  organicTier: OrganicTier | null;
  isPerformanceTier: boolean;
  tiers: TierOption[];
  tierChanged: boolean;
  onDaysChange: (tierId: string, days: number | null) => void;
  onExtend: (tierId: string, extraDays: number | null) => void;
  onTierChange: (newTierId: string) => void;
  onClear: () => void;
  isLoading: boolean;
}) {
  const totalDays = getDaysBetween(protection.created_at, protection.ends_at);
  const [extending, setExtending] = useState(false);
  const [extraDays, setExtraDays] = useState('');

  const parsedExtra = parseInt(extraDays, 10);
  const isExtraValid = parsedExtra >= 1 && parsedExtra <= 365;

  const handleExtraDaysChange = (value: string) => {
    setExtraDays(value);
    const parsed = parseInt(value, 10);
    if (parsed >= 1 && parsed <= 365) {
      onExtend(protection.tier_id, parsed);
    } else {
      onExtend(protection.tier_id, null);
    }
  };

  const endDate = new Date(protection.ends_at);
  if (isExtraValid) {
    endDate.setDate(endDate.getDate() + parsedExtra);
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      {/* Tier selector */}
      {isPerformanceTier ? (
        <div className="tw:flex tw:items-center tw:justify-between">
          <div className="tw:flex tw:flex-col">
            <Text variant="body-sm" weight="semibold">
              Performance tier
            </Text>
            <Text variant="body-sm" color="muted">
              Automatic based on performance
            </Text>
          </div>
          <Badge variant="success" size="sm">
            {protection.tier_name}
          </Badge>
        </div>
      ) : (
        <>
          <div className="tw:flex tw:items-center tw:justify-between">
            <Text variant="body-sm" weight="semibold">
              Manually assigned tier
            </Text>
            <div className="tw:w-32">
              <AffiliateTierSelect value={displayTierId} tiers={tiers} onValueChange={onTierChange} />
            </div>
          </div>
          {tiers.find((t) => t.id === displayTierId)?.requiresManualProjectApproval && (
            <Text variant="body-sm" color="muted" className="tw:-mt-1 tw:text-right">
              This tier requires approval — the affiliate will be approved on save.
            </Text>
          )}
        </>
      )}

      {tierChanged ? (
        <ProtectionPeriodInput
          key={displayTierId}
          tierId={displayTierId}
          initialDays={null}
          createdAt={null}
          onDaysChange={onDaysChange}
          onClear={onClear}
          isLoading={isLoading}
        />
      ) : (
        <>
          {/* Protection period + extend */}
          <Card variant="subcard" className="tw:px-4 tw:py-3 tw:gap-3">
            <div className="tw:flex tw:items-center tw:justify-between">
              <Text variant="body-sm" weight="semibold">
                Protection period
              </Text>
              <Text variant="body-md" weight="semibold">
                {totalDays} day{totalDays !== 1 ? 's' : ''}
              </Text>
            </div>

            {!extending ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setExtending(true)} className="tw:w-full">
                <Plus size={14} />
                Extend period
              </Button>
            ) : (
              <div className="tw:flex tw:items-center tw:gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setExtending(false);
                    setExtraDays('');
                    onExtend(protection.tier_id, null);
                  }}
                  className="tw:text-muted-foreground hover:tw:text-foreground tw:transition-colors"
                  aria-label="Cancel extension"
                >
                  <X size={16} />
                </button>
                <Text variant="body-sm" weight="semibold" className="tw:whitespace-nowrap">
                  Extend by
                </Text>
                <div className="tw:w-24 tw:shrink-0 tw:ml-auto">
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    size="sm"
                    value={extraDays}
                    onChange={(e) => handleExtraDaysChange(e.target.value)}
                    placeholder="—"
                    style={{ textAlign: 'right' }}
                    rightSlot={
                      <Text variant="body-sm" color="muted">
                        days
                      </Text>
                    }
                  />
                </div>
              </div>
            )}
          </Card>

          {/* Date reference + remove */}
          <div className="tw:flex tw:items-center tw:gap-2">
            <button
              type="button"
              onClick={onClear}
              disabled={isLoading}
              className="tw:flex tw:items-center tw:gap-1.5 tw:text-error-main hover:tw:text-error-main/80 tw:transition-colors"
            >
              <X size={16} />
              <Text variant="body-sm" className="tw:text-inherit">
                Remove protection
              </Text>
            </button>
            <Text
              variant="body-md"
              color={isExtraValid ? undefined : 'muted'}
              className={cn(
                'tw:ml-auto tw:whitespace-nowrap tw:flex tw:items-center tw:gap-1.5 tw:transition-colors',
                isExtraValid && 'tw:text-fuul-blue',
              )}
            >
              {formatDate(new Date(protection.created_at))}{' '}
              <ArrowRight className="tw:inline tw:h-3 tw:w-3" aria-hidden="true" /> {formatDate(endDate)}
            </Text>
          </div>
        </>
      )}

      {protection.is_below_effective && organicTier && (
        <Alert
          variant="info"
          icon={<Info size={16} />}
          title={`Protection active at ${protection.tier_name} (below current tier ${organicTier.name})`}
          description="Since the affiliate already qualifies for a higher tier through audience conditions, this protection is dormant. Assigning a protection at or above the current tier will replace it."
        />
      )}
    </div>
  );
}

export function TierProtectionCard({
  projectId,
  organicTier,
  currentTier,
  protections,
  tiers,
  onPendingChange,
  isLoading,
  addProtectionLabel = 'Add tier protection',
  addProtectionHelper,
}: TierProtectionCardProps) {
  const [addingProtection, setAddingProtection] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  // Tracks whether the user cleared an existing protection (pending save)
  const [cleared, setCleared] = useState(false);
  // Tracks tier override when user changes tier on an existing protection
  const [overrideTierId, setOverrideTierId] = useState<string | null>(null);

  // Reset local state when protections change (e.g., after save)
  useEffect(() => {
    setAddingProtection(false);
    setSelectedTierId(null);
    setCleared(false);
    setOverrideTierId(null);
  }, [protections]);

  const hasMatchingProtection = protections.some((p) => !p.is_below_effective);
  const hasMultipleProtections = protections.length > 1;
  // Treat as "no protections" when the user cleared them locally
  const showAsEmpty = protections.length === 0 || cleared;

  const handleDaysChange = (tierId: string, days: number | null) => {
    if (days !== null) {
      onPendingChange({ tier_id: tierId, days }, false, false);
    } else {
      // Days missing while the user is mid-add / mid-override → incomplete
      const incomplete = (addingProtection && !!selectedTierId) || overrideTierId !== null;
      onPendingChange(null, false, incomplete);
    }
  };

  const handleExtend = (tierId: string, extraDays: number | null) => {
    if (extraDays !== null) {
      onPendingChange({ tier_id: tierId, days: extraDays, isExtension: true }, false, false);
    } else {
      onPendingChange(null, false, false);
    }
  };

  const handleClear = () => {
    setAddingProtection(false);
    setSelectedTierId(null);
    setCleared(true);
    setOverrideTierId(null);
    onPendingChange(null, true, false);
  };

  const handleTierChange = (newTierId: string) => {
    const existingProtection = protections[0];
    if (existingProtection && newTierId === existingProtection.tier_id) {
      setOverrideTierId(null);
      onPendingChange(null, false, false);
      return;
    }

    setOverrideTierId(newTierId);
    // Reset pending — user must set new days for the new tier
    onPendingChange(null, false, true);
  };

  // Compute the tier the affiliate will end up at if changes are saved.
  // Mirrors backend logic: protection wins when its rank <= organic tier rank.
  const computePredictedTier = (): { id: string; name: string } | null => {
    const pickedTierId = addingProtection ? selectedTierId : overrideTierId;
    if (pickedTierId) {
      const picked = tiers.find((t) => t.id === pickedTierId);
      if (!picked) return currentTier;
      if (!organicTier || picked.rank <= organicTier.rank) return picked;
      return organicTier;
    }
    if (cleared) return organicTier;
    return currentTier;
  };

  const predictedTier = computePredictedTier();
  const tierWillChange = (predictedTier?.id ?? null) !== (currentTier?.id ?? null);
  const isDefaultTierId = (tierId: string | undefined) =>
    !!tierId && tiers.find((t) => t.id === tierId)?.audienceId === null;

  return (
    <Card className="tw:p-4">
      {/* Header */}
      <div className="tw:flex tw:items-center tw:justify-between tw:pb-3 tw:border-b tw:border-border">
        <div className="tw:flex tw:flex-col tw:gap-0.5">
          <Text variant="body-sm" color="muted" weight="semibold" className="tw:uppercase tw:tracking-wide">
            Current Tier
          </Text>
          {hasMatchingProtection && !cleared ? (
            <div className="tw:flex tw:items-center tw:gap-1.5 tw:text-fuul-blue">
              <Shield size={14} />
              <Text variant="body-sm" className="tw:text-fuul-blue">
                Protected (assigned tier)
              </Text>
            </div>
          ) : organicTier ? (
            <div className="tw:flex tw:items-center tw:gap-1.5 tw:text-success-main">
              <TrendingUp size={14} />
              <Text variant="body-sm" className="tw:text-success-main">
                By audience conditions
              </Text>
            </div>
          ) : null}
        </div>
        {tierWillChange ? (
          <div className="tw:flex tw:items-center tw:gap-2">
            <Tooltip content={isDefaultTierId(currentTier?.id) ? DEFAULT_TIER_TOOLTIP : ''} side="top">
              <Badge variant={currentTier ? 'outline' : 'outline'} className="tw:opacity-60 tw:line-through">
                {currentTier?.name ?? 'No tier'}
              </Badge>
            </Tooltip>
            <ArrowRight size={14} className="tw:text-fuul-blue" aria-hidden="true" />
            <Tooltip content={isDefaultTierId(predictedTier?.id) ? DEFAULT_TIER_TOOLTIP : ''} side="top">
              <Badge variant={predictedTier ? 'default' : 'outline'} className="tw:text-fuul-blue tw:border-fuul-blue">
                {predictedTier?.name ?? 'No tier'}
              </Badge>
            </Tooltip>
          </div>
        ) : (
          <Tooltip content={isDefaultTierId(currentTier?.id) ? DEFAULT_TIER_TOOLTIP : ''} side="top">
            <Badge variant={currentTier ? 'default' : 'outline'}>{currentTier?.name ?? 'No tier'}</Badge>
          </Tooltip>
        )}
      </div>

      {/* Body */}
      <div className="tw:flex tw:flex-col tw:divide-y tw:divide-border">
        {predictedTier && (
          <div className="tw:py-5">
            <TierIncentivesCollapsible projectId={projectId} tierId={predictedTier.id} />
          </div>
        )}

        {showAsEmpty && !addingProtection && (
          <div className="tw:pt-3 tw:flex tw:flex-col tw:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAddingProtection(true)}
              className="tw:w-full"
            >
              <Shield size={14} />
              {addProtectionLabel}
            </Button>
            {addProtectionHelper && (
              <Text variant="body-sm" color="muted">
                {addProtectionHelper}
              </Text>
            )}
          </div>
        )}

        {showAsEmpty && addingProtection && (
          <div className="tw:flex tw:flex-col tw:gap-3 tw:pt-3">
            <div className="tw:flex tw:items-center tw:justify-between">
              <Text variant="body-sm" weight="semibold">
                Manually assigned tier
              </Text>
              <div className="tw:w-32">
                <AffiliateTierSelect
                  value={selectedTierId ?? undefined}
                  tiers={tiers}
                  onValueChange={(value) => {
                    setSelectedTierId(value);
                    onPendingChange(null, false, !!value);
                  }}
                />
              </div>
            </div>

            {selectedTierId && tiers.find((t) => t.id === selectedTierId)?.requiresManualProjectApproval && (
              <Text variant="body-sm" color="muted" className="tw:-mt-1 tw:text-right">
                This tier requires approval — the affiliate will be approved on save.
              </Text>
            )}

            {selectedTierId && (
              <ProtectionPeriodInput
                key={selectedTierId}
                tierId={selectedTierId}
                initialDays={null}
                createdAt={null}
                onDaysChange={handleDaysChange}
                onClear={() => {
                  setAddingProtection(false);
                  setSelectedTierId(null);
                  onPendingChange(null, cleared, false);
                }}
                isLoading={isLoading}
              />
            )}
          </div>
        )}

        {!cleared &&
          protections.map((protection) => {
            const isPerformanceTier = !protection.is_below_effective && hasMultipleProtections;
            const displayTierId = overrideTierId ?? protection.tier_id;
            return (
              <div key={protection.tier_id} className="tw:pt-3">
                <ProtectionSection
                  protection={protection}
                  displayTierId={displayTierId}
                  organicTier={organicTier}
                  isPerformanceTier={isPerformanceTier}
                  tiers={tiers}
                  tierChanged={overrideTierId !== null}
                  onDaysChange={handleDaysChange}
                  onExtend={handleExtend}
                  onTierChange={handleTierChange}
                  onClear={handleClear}
                  isLoading={isLoading}
                />
              </div>
            );
          })}
      </div>
    </Card>
  );
}
